import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, Mail, Instagram } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionTypes = [
  "1:1 Personal Session",
  "Group Session",
  "General Awareness Talk",
];

const BookingSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    session_type: "1:1 Personal Session",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! I'll get back to you within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        session_type: "1:1 Personal Session",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="grain relative overflow-hidden bg-[#0A2E23] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="relative z-10 mx-auto max-w-[1400px]">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-secondary md:text-sm">
            Let's Get You Booked
          </p>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-tight tracking-tight text-primary-foreground md:text-6xl">
            Ready to understand your money better?
          </h2>
          <p className="mt-6 max-w-xl text-lg font-light text-primary-foreground/70">
            Tell me a bit about what you need — I'll get back to you within 24 hours.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Lead form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="lead-form"
              className="rounded-sm border border-primary-foreground/15 bg-primary/40 p-8 backdrop-blur-sm md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-primary-foreground/80">
                    Your name
                  </label>
                  <Input
                    data-testid="lead-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Priya Sharma"
                    className="h-12 border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/40"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-primary-foreground/80">
                    Email
                  </label>
                  <Input
                    data-testid="lead-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="h-12 border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/40"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-primary-foreground/80">
                    Phone (optional)
                  </label>
                  <Input
                    data-testid="lead-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 ..."
                    className="h-12 border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/40"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-primary-foreground/80">
                    I'm interested in
                  </label>
                  <Select value={form.session_type} onValueChange={update("session_type")}>
                    <SelectTrigger
                      data-testid="lead-session-type"
                      className="h-12 border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sessionTypes.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`session-opt-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-primary-foreground/80">
                  Tell me what you need
                </label>
                <Textarea
                  data-testid="lead-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Your goals, audience size for talks, or anything else..."
                  className="border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/40"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="lead-submit"
                className="group mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-secondary px-8 py-4 text-base font-semibold text-secondary-foreground transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
              >
                {loading ? "Sending..." : "Confirm My Interest"}
                {!loading && (
                  <ArrowRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                )}
              </button>

              <p className="mt-4 flex items-center gap-2 text-sm text-primary-foreground/55">
                <ShieldCheck size={16} />
                Your details are only used to schedule your session and will never be
                shared.
              </p>
            </form>
          </Reveal>

          {/* Direct contact / next steps panel */}
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="flex h-full flex-col justify-between rounded-sm border border-secondary/40 bg-secondary p-8 md:p-10">
              <div>
                <Clock size={32} className="text-secondary-foreground" />
                <h3 className="mt-6 font-display text-3xl font-medium text-secondary-foreground">
                  What happens next?
                </h3>
                <ul className="mt-6 space-y-4">
                  {[
                    "Share your details and what you're looking for.",
                    "I personally review every enquiry — no bots, no sales team.",
                    "You'll hear back within 24 hours to lock in a time that suits you.",
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                        {i + 1}
                      </span>
                      <span className="text-base font-light leading-relaxed text-secondary-foreground/85">
                        {step}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-10 space-y-3">
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="booking-email-btn"
                  className="group flex items-center justify-between rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground transition-transform duration-300 hover:scale-[1.02]"
                >
                  <span className="flex items-center gap-3">
                    <Mail size={18} /> Email me directly
                  </span>
                  <ArrowRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="booking-instagram-btn"
                  className="group flex items-center justify-between rounded-full border border-primary/30 px-7 py-4 text-base font-semibold text-secondary-foreground transition-colors duration-300 hover:border-primary"
                >
                  <span className="flex items-center gap-3">
                    <Instagram size={18} /> DM on Instagram
                  </span>
                  <ArrowRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
